import React from 'react';
import { Box, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { jaJP } from '@mui/x-date-pickers/locales';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';

import {
  CustomDay,
  customCalendarHeaderSx,
  CustomCalendarHeaderStyles,
} from '@/components/layouts';

export interface DateRangePickerProps {
  startDate: string;
  endDate: string;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  startDateError?: boolean;
  endDateError?: boolean;
  startDateHelperText?: string;
  endDateHelperText?: string;
}

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  startDateError = false,
  endDateError = false,
  startDateHelperText,
  endDateHelperText,
}) => {
  const [isShowingStartPicker, setIsShowingStartPicker] = React.useState(false);
  const [isShowingEndPicker, setIsShowingEndPicker] = React.useState(false);

  const handleOpenStartPicker = React.useCallback(() => {
    setIsShowingStartPicker((prev) => (prev ? prev : true));
  }, []);
  const handleOpenEndPicker = React.useCallback(() => {
    setIsShowingEndPicker((prev) => (prev ? prev : true));
  }, []);

  const isStartDateInvalid = !startDate;
  const isEndDateInvalid = !endDate;

  const startDateDisplayError = startDateError || isStartDateInvalid;
  const endDateDisplayError = endDateError || isEndDateInvalid;
  const startDateDisplayHelperText =
    startDateHelperText || (isStartDateInvalid ? '開始日は必須入力項目です。' : '');
  const endDateDisplayHelperText =
    endDateHelperText || (isEndDateInvalid ? '終了日は必須入力項目です。' : '');

  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale='ja'
      localeText={jaJP.components.MuiLocalizationProvider.defaultProps.localeText}
    >
      <CustomCalendarHeaderStyles />

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        <DatePicker
          label='開始日'
          format='YYYY/MM/DD'
          views={['year', 'month', 'day']}
          value={startDate ? dayjs(startDate) : null}
          onChange={(newValue) => {
            onStartDateChange(newValue ? newValue.format('YYYY-MM-DD') : '');
          }}
          maxDate={endDate ? dayjs(endDate) : undefined}
          slots={{ day: CustomDay }}
          open={isShowingStartPicker}
          onOpen={() => setIsShowingStartPicker(true)}
          onClose={() => setIsShowingStartPicker(false)}
          slotProps={{
            textField: {
              required: true,
              error: startDateDisplayError,
              helperText: startDateDisplayHelperText,
              inputProps: {
                readOnly: true,
              },
              sx: { flex: 1 },
              onClick: handleOpenStartPicker,
              onFocus: undefined,
            },
            calendarHeader: { sx: customCalendarHeaderSx },
          }}
        />

        <Typography variant='body1' sx={{ mx: 1, fontSize: '1.5rem', fontWeight: 500 }}>
          ~
        </Typography>

        <DatePicker
          label='終了日'
          format='YYYY/MM/DD'
          views={['year', 'month', 'day']}
          value={endDate ? dayjs(endDate) : null}
          onChange={(newValue) => {
            onEndDateChange(newValue ? newValue.format('YYYY-MM-DD') : '');
          }}
          minDate={startDate ? dayjs(startDate) : undefined}
          slots={{ day: CustomDay }}
          open={isShowingEndPicker}
          onOpen={() => setIsShowingEndPicker(true)}
          onClose={() => setIsShowingEndPicker(false)}
          slotProps={{
            textField: {
              required: true,
              error: endDateDisplayError,
              helperText: endDateDisplayHelperText,
              inputProps: {
                readOnly: true,
              },
              sx: { flex: 1 },
              onClick: handleOpenEndPicker,
              onFocus: undefined,
            },
            calendarHeader: { sx: customCalendarHeaderSx },
          }}
        />
      </Box>
    </LocalizationProvider>
  );
};
